import React, { useState } from 'react';
import './LoginModal.css';
import { useTranslation } from '../contexts/TranslationContext';
import GlobalSportLogo from './GlobalSportLogo';
import { AUTH_CONFIG } from '../config/api';

interface LoginModalProps {
  onSubmit: (msisdn: string) => Promise<boolean>;
  onClose: () => void;
}

const COUNTRY_PREFIX = `+${AUTH_CONFIG.COUNTRY_CODE}`;

const LoginModal: React.FC<LoginModalProps> = ({ onSubmit, onClose }) => {
  const { t } = useTranslation();
  const [phone, setPhone] = useState(COUNTRY_PREFIX);
  const [phoneError, setPhoneError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateMobileNumber = (phoneNumber: string): boolean => {
    const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');

    if (!cleanNumber.startsWith(COUNTRY_PREFIX)) {
      return false;
    }

    const digits = cleanNumber.substring(COUNTRY_PREFIX.length);
    return (
      digits.length === AUTH_CONFIG.PHONE_DIGIT_LENGTH &&
      new RegExp(`^\\d{${AUTH_CONFIG.PHONE_DIGIT_LENGTH}}$`).test(digits)
    );
  };

  const buildMsisdn = (phoneNumber: string): string => {
    const digits = phoneNumber.replace(/[^\d+]/g, '').substring(COUNTRY_PREFIX.length);
    return `${AUTH_CONFIG.COUNTRY_CODE}${digits}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateMobileNumber(phone)) {
      setPhoneError(`Please enter a valid ${AUTH_CONFIG.PHONE_DIGIT_LENGTH}-digit mobile number`);
      return;
    }

    setIsSubmitting(true);
    setPhoneError('');

    try {
      await onSubmit(buildMsisdn(phone));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <div className="modal-header">
          <div className="modal-logo-custom">
            <GlobalSportLogo size="medium" animated={true} />
          </div>
          <h1 className="modal-title">{t('login.welcome')}</h1>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="input-group">
            <label className="input-label">{t('login.phone.label')}</label>
            <div className="phone-input-wrapper">
              <span className="phone-prefix">{COUNTRY_PREFIX}</span>
              <input
                type="tel"
                value={phone.replace(COUNTRY_PREFIX, '')}
                onChange={(e) => {
                  let value = e.target.value.replace(/[^\d]/g, '');
                  if (value.length > AUTH_CONFIG.PHONE_DIGIT_LENGTH) {
                    value = value.substring(0, AUTH_CONFIG.PHONE_DIGIT_LENGTH);
                  }
                  setPhone(COUNTRY_PREFIX + value);
                  if (phoneError) {
                    setPhoneError('');
                  }
                }}
                className={`phone-input ${phoneError ? 'error' : ''}`}
                placeholder="76521776"
                required
                disabled={isSubmitting}
              />
            </div>
            {phoneError && (
              <div className="error-message">{phoneError}</div>
            )}
          </div>

          <button 
            type="submit" 
            className="send-otp-button"
            disabled={!validateMobileNumber(phone) || isSubmitting}
            style={{
              background: 'var(--gradient-primary)',
              opacity: !validateMobileNumber(phone) || isSubmitting ? 0.6 : 1
            }}
          >
            <span>{isSubmitting ? 'Sending...' : t('login.send.otp')}</span>
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
              <path d="M4 10L16 10M10 4L16 10L10 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </form>

        <div className="security-notice">
          <div className="security-icon">🔒</div>
          <span>{t('login.security')}</span>
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
